/**
 * 主軸カテゴリーと商品カタログの紐付けヘルパー (v3.2.10 / T5 Phase 4)。
 *
 * 紐付け規則:
 *   1. `category.catalogIds` が設定されていれば、その catalog.id 一致を採用
 *   2. 未設定 (空配列 or undefined) なら、後方互換で `category.name === catalog.category` の同名一致
 */
import type { ProductCategory } from '@goenhub/shared';

import type { MergedCatalogItem } from '@/hooks/useProductCatalogs';

/**
 * 指定カテゴリーに「属する」と判定される catalog items を返す。
 */
export function filterItemsForCategory(
  items: ReadonlyArray<MergedCatalogItem>,
  category: Pick<ProductCategory, 'name' | 'catalogIds'>,
): MergedCatalogItem[] {
  const linkedIds = category.catalogIds ?? [];
  if (linkedIds.length > 0) {
    const set = new Set(linkedIds);
    return items.filter((it) => set.has(it.catalogId));
  }
  // 後方互換: 同名一致
  return items.filter((it) => it.catalogCategory === category.name);
}

/**
 * productId → 主軸カテゴリー ID の対応表を作る (開拓顧客加算率をカテゴリー別に引くため)。
 * catalogIds / linkedBrandNames の一致を優先し、無ければカテゴリー名 = catalog.category の同名一致。
 */
export function buildProductIdToCategoryId(
  items: ReadonlyArray<Pick<MergedCatalogItem, 'id' | 'catalogId' | 'catalogCategory'>>,
  categories: ReadonlyArray<
    Pick<ProductCategory, 'name' | 'catalogIds' | 'linkedBrandNames'> & { id: string }
  >,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const p of items) {
    const byCatalog = categories.find(
      (c) =>
        (c.catalogIds && c.catalogIds.includes(p.catalogId)) ||
        (c.linkedBrandNames && c.linkedBrandNames.includes(p.catalogCategory)),
    );
    if (byCatalog) {
      map.set(p.id, byCatalog.id);
      continue;
    }
    const byName = categories.find((c) => c.name === p.catalogCategory);
    if (byName) map.set(p.id, byName.id);
  }
  return map;
}
